import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { privacyAPI } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { paths } from '../routes/paths';
import '../styles/legal.css';

// Cookie consent (GDPR, Figma "Cookie consent"): a banner on a first visit, and the "Cookie preferences" window (also opened from the footer and
// from Privacy & data rights). Strictly necessary cookies are always on; analytics and marketing stay off until the person says yes.
// The choice is kept in this browser for 12 months; for a signed-in person it is also written to their account (a log, for the record).
// Anything that wants to measure or advertise must ask useConsent() first.
const KEY = 'cookieConsent';
const YEAR_MS = 365 * 24 * 3600 * 1000;

const read = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY));
    return saved && Date.now() - new Date(saved.savedAt).getTime() < YEAR_MS ? saved : null;
  } catch { return null; }
};

const ConsentContext = createContext({ consent: null, openPreferences: () => {} });
export const useConsent = () => useContext(ConsentContext);

function Switch({ on, locked, label, onChange }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={locked} className={`lg-switch${on ? ' on' : ''}`} onClick={() => onChange(!on)}><i /></button>;
}

function Preferences({ initial, onSave, onClose }) {
  const [analytics, setAnalytics] = useState(initial?.analytics ?? false);
  const [marketing, setMarketing] = useState(initial?.marketing ?? false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="cc-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cc-modal" role="dialog" aria-modal="true" aria-labelledby="cc-title">
        <button type="button" className="cc-x" aria-label="Close" onClick={onClose}>×</button>
        <h2 id="cc-title">Cookie preferences</h2>
        <p className="cc-lead">You control everything except strictly necessary cookies. Your choice is stored for 12 months and you can change it any time from the footer.</p>
        <div className="cc-rows">
          <div className="cc-row">
            <div><b>Strictly necessary <span className="cc-pill">Always on</span></b><span>Sign-in, checkout, fraud prevention and security. These cannot be switched off.</span></div>
            <Switch on locked label="Strictly necessary" onChange={() => {}} />
          </div>
          <div className="cc-row">
            <div><b>Analytics</b><span>Anonymous usage statistics that help us improve discovery and checkout.</span></div>
            <Switch on={analytics} label="Analytics" onChange={setAnalytics} />
          </div>
          <div className="cc-row">
            <div><b>Marketing</b><span>Measuring event promotions. Off unless you turn it on.</span></div>
            <Switch on={marketing} label="Marketing" onChange={setMarketing} />
          </div>
        </div>
        <div className="cc-foot">
          <Link to={paths.privacy} onClick={onClose}>Read the privacy policy</Link>
          <div>
            <button type="button" className="cc-btn" onClick={() => onSave(false, false)}>Reject non-essential</button>
            <button type="button" className="cc-btn cc-dark" onClick={() => onSave(analytics, marketing)}>Save preferences</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ConsentProvider({ children }) {
  const { user } = useAuth();
  const [consent, setConsent] = useState(read);
  const [open, setOpen] = useState(false);

  const save = useCallback((analytics, marketing) => {
    const next = { analytics, marketing, savedAt: new Date().toISOString() };
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* the choice then lasts until the page is closed */ }
    setConsent(next);
    setOpen(false);
    if (user) privacyAPI.saveConsent({ analytics, marketing }).catch(() => {});
  }, [user]);

  const value = useMemo(() => ({ consent, openPreferences: () => setOpen(true) }), [consent]);

  return (
    <ConsentContext.Provider value={value}>
      {children}
      {!consent && !open && (
        <div className="cc-banner" role="region" aria-label="Cookies">
          <div>
            <b>We use cookies to run this site</b>
            <span>Essential cookies keep the site working. Analytics cookies are optional and stay off until you accept. You can change this any time in cookie preferences.</span>
          </div>
          <div className="cc-actions">
            <button type="button" className="cc-link" onClick={() => setOpen(true)}>Cookie preferences</button>
            <button type="button" className="cc-btn" onClick={() => save(false, false)}>Reject non-essential</button>
            <button type="button" className="cc-btn cc-dark" onClick={() => save(true, true)}>Accept all</button>
          </div>
        </div>
      )}
      {open && <Preferences initial={consent} onSave={save} onClose={() => setOpen(false)} />}
    </ConsentContext.Provider>
  );
}
